/**
 * useWebSocket - Custom React hook for real-time dashboard updates
 * Connects to ResiliencyHUB WebSocket server for live notifications
 * 
 * Usage:
 *   const { isConnected, lastEvent, subscribe } = useWebSocket();
 *   
 *   useEffect(() => {
 *     const unsubscribe = subscribe('plan_approved', (data) => {
 *       console.log('Plan approved:', data);
 *       refetchData();
 *     });
 *     return unsubscribe;
 *   }, [subscribe]);
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Event types that match backend WSEventType
export const WS_EVENTS = {
  // Plan events
  PLAN_SUBMITTED: 'plan_submitted',
  PLAN_APPROVED: 'plan_approved',
  PLAN_REJECTED: 'plan_rejected',
  PLAN_REVISION_REQUIRED: 'plan_revision_required',
  
  // Incident events
  INCIDENT_ACTIVATED: 'incident_activated',
  INCIDENT_RESOLVED: 'incident_resolved',
  
  // Crisis events
  CRISIS_ALERT_SENT: 'crisis_alert_sent',
  
  // Task events
  TASK_CREATED: 'task_created',
  TASK_UPDATED: 'task_updated',
  TASK_COMPLETED: 'task_completed',
  
  // PACA events
  PACA_AUTO_CREATED: 'paca_auto_created',
  PACA_APPROVED: 'paca_approved',
  
  // Dashboard events
  DASHBOARD_UPDATE: 'dashboard_update',
  STATS_UPDATE: 'stats_update',
};

export function useWebSocket(options = {}) {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  
  const wsRef = useRef(null);
  const subscribersRef = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = options.maxReconnectAttempts || 5;
  const reconnectDelay = options.reconnectDelay || 3000;
  
  // Build WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    if (!user?.org_id) return null;
    
    // Get the base URL from environment
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    // Convert HTTP(S) to WS(S)
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    
    // Build WebSocket URL with query params
    const params = new URLSearchParams();
    if (user.id) params.append('user_id', user.id);
    if (user.role) params.append('role', user.role);
    
    return `${wsProtocol}://${wsHost}/api/ws/${user.org_id}?${params.toString()}`;
  }, [user]);
  
  // Handle incoming messages
  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      setLastEvent(data);
      
      // Notify all subscribers for this event type
      if (data.type && subscribersRef.current.has(data.type)) {
        const callbacks = subscribersRef.current.get(data.type);
        callbacks.forEach(callback => {
          try {
            callback(data.data, data);
          } catch (err) {
            console.error('WebSocket subscriber error:', err);
          }
        });
      }
      
      // Also notify 'all' subscribers
      if (subscribersRef.current.has('all')) {
        const callbacks = subscribersRef.current.get('all');
        callbacks.forEach(callback => {
          try {
            callback(data.data, data);
          } catch (err) {
            console.error('WebSocket subscriber error:', err);
          }
        });
      }
      
      // Show toast for important events
      if (data.type === WS_EVENTS.CRISIS_ALERT_SENT) {
        toast.error(`Crisis Alert: ${data.data?.erp_name}`, {
          duration: 10000,
          description: `Severity: ${data.data?.severity?.toUpperCase()}`
        });
      } else if (data.type === WS_EVENTS.INCIDENT_ACTIVATED) {
        toast.warning(`Incident Activated: ${data.data?.erp_name}`, {
          duration: 8000
        });
      } else if (data.type === WS_EVENTS.PLAN_APPROVED) {
        toast.success(`Plan Approved: ${data.data?.task_title}`, {
          duration: 5000
        });
      } else if (data.type === WS_EVENTS.PACA_AUTO_CREATED) {
        toast.info(`New PACA Item: ${data.data?.title}`, {
          duration: 8000,
          description: `Source: ${data.data?.source} | Severity: ${data.data?.severity?.toUpperCase()}`
        });
      }
    } catch (err) {
      console.error('WebSocket message parse error:', err);
    }
  }, []);
  
  // Connect to WebSocket
  const connect = useCallback(() => {
    const wsUrl = getWebSocketUrl();
    if (!wsUrl) return;
    
    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttemptsRef.current = 0;
        console.log('WebSocket connected');
      };
      
      ws.onmessage = handleMessage;
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionError('Connection error');
      };
      
      ws.onclose = (event) => {
        setIsConnected(false);
        console.log('WebSocket disconnected:', event.code, event.reason);
        
        // Attempt to reconnect if not a clean close
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`WebSocket reconnecting (attempt ${reconnectAttemptsRef.current})...`);
            connect();
          }, reconnectDelay * reconnectAttemptsRef.current);
        }
      };
      
      wsRef.current = ws;
    } catch (err) {
      console.error('WebSocket connection failed:', err);
      setConnectionError(err.message);
    }
  }, [getWebSocketUrl, handleMessage, maxReconnectAttempts, reconnectDelay]);
  
  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);
  
  // Subscribe to events
  const subscribe = useCallback((eventType, callback) => {
    if (!subscribersRef.current.has(eventType)) {
      subscribersRef.current.set(eventType, new Set());
    }
    subscribersRef.current.get(eventType).add(callback);
    
    // Return unsubscribe function
    return () => {
      const callbacks = subscribersRef.current.get(eventType);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          subscribersRef.current.delete(eventType);
        }
      }
    };
  }, []);
  
  // Send keep-alive ping
  const sendPing = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send('ping');
    }
  }, []);
  
  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (user?.org_id) {
      connect();
      
      // Set up ping interval to keep connection alive
      const pingInterval = setInterval(sendPing, 30000);
      
      return () => {
        clearInterval(pingInterval);
        disconnect();
      };
    }
  }, [user?.org_id, connect, disconnect, sendPing]);
  
  return {
    isConnected,
    lastEvent,
    connectionError,
    subscribe,
    connect,
    disconnect,
    sendPing
  };
}

export default useWebSocket;
